import { SimpleEmitter } from '../../clients/abstracts/SimpleEmitter.js';
import { matchExpr } from '../../clients/abstracts/util.js';
import { ExprEngine } from '../../flashql/eval/ExprEngine.js';
import { Transformer } from '../../lang/Transformer.js';
import { LinkedQlWal } from '../timeline/LinkedQlWal.js';
import { registry } from "../../lang/registry.js";
import { _eq } from "../../lang/abstracts/util.js";
import { SYSTEM_TAG } from '../SYSTEM.js';

export class QueryWindow extends SimpleEmitter {

    static analyseQuery(query) {

        const analysis = {
            hasSubqueryExprsInSelect: 0,
            hasSubqueryExprsInWhere: 0,
            hasSubqueryExprsInOrderBy: 0,
            hasSubqueryExprs: 0,
            hasWindowFunctions: false,
            hasAggrFunctions: false,
            hasGroupByClause: false,
            hasOrderByClause: false,
            hasOffsetClause: false,
            hasLimitClause: false,
            fromItemsBySchema: {},
            fromItemsByAlias: {},
            isSingleTable: false,
        };

        const isLocallyResolvedTableRef = (tableRef) => {
            if (!(tableRef instanceof registry.TableRef1)) return false;
            const resolution = typeof tableRef.resolution === 'function'
                ? tableRef.resolution()
                : undefined;
            // Newer parser nodes may omit explicit resolution metadata.
            return resolution === undefined || resolution === 'default';
        };

        query.walkTree((n) => {
            // Aggregate expressions?
            if (n instanceof registry.AggrCallExpr) {
                if (n.overClause()) {
                    analysis.hasWindowFunctions = true;
                } else analysis.hasAggrFunctions = true;
            }
            // Subquery/derived query expresions?
            else if (n instanceof registry.DerivedQuery) {
                const relationHashes = new Set;
                grepFromItems(n.expr(), analysis.fromItemsBySchema, relationHashes);
                const resulutionLevel = relationHashes.size || !(n.expr() instanceof registry.SelectStmt)
                    ? 2 : (n.isCorrelated() ? 1 : 0);
                if (resulutionLevel === 0) return;
                // Publish
                if (analysis.hasSubqueryExprs < resulutionLevel) {
                    analysis.hasSubqueryExprs = resulutionLevel;
                }
                if (analysis.hasSubqueryExprsInSelect < resulutionLevel
                    && query.selectList().containsNode(n)) {
                    analysis.hasSubqueryExprsInSelect = resulutionLevel;
                } else if (analysis.hasSubqueryExprsInWhere < resulutionLevel
                    && query.whereClause()?.containsNode(n)) {
                    analysis.hasSubqueryExprsInWhere = resulutionLevel;
                } else if (analysis.hasSubqueryExprsInOrderBy < resulutionLevel
                    && query.orderByClause()?.containsNode(n)) {
                    analysis.hasSubqueryExprsInOrderBy = resulutionLevel;
                }
            }
            // Enter FromItem:
            else if (n instanceof registry.FromItem) {
                // Aliases are expected
                //  - except for a FROM (subquery) scenario, where it's optional
                const alias = n.alias()?.value() || '';
                if (n.expr() instanceof registry.DerivedQuery) {
                    const relationHashes = new Set;
                    grepFromItems(n.expr().expr(), analysis.fromItemsBySchema, relationHashes);
                    analysis.fromItemsByAlias[alias] = relationHashes;
                } else if (isLocallyResolvedTableRef(n.expr())) {
                    const relationHashes = new Set;
                    acquireTableRef(n.expr(), analysis.fromItemsBySchema, relationHashes);
                    analysis.fromItemsByAlias[alias] = relationHashes;
                } else {
                    analysis.fromItemsByAlias[alias] = new Set;
                }
            } else return n;
        });

        function grepFromItems(query, fromItemsBySchema, relationHashes = null) {
            query.walkTree((n) => {
                if (n instanceof registry.FromItem
                    && isLocallyResolvedTableRef(n.expr())) {
                    acquireTableRef(n.expr(), fromItemsBySchema, relationHashes);
                } else return n;
            }, true);
        }

        function acquireTableRef(tableRef, fromItemsBySchema, relationHashes = null) {
            const tableName = tableRef.value();
            const namespaceName = tableRef.qualifier().value(); // Both name and qualifier are expected
            fromItemsBySchema[namespaceName] = [].concat(fromItemsBySchema[namespaceName] || []).concat(tableName);
            if (relationHashes) relationHashes.add(JSON.stringify([namespaceName, tableName]));
        }

        if (query.groupByClause()) analysis.hasGroupByClause = true;
        if (query.orderByClause()) analysis.hasOrderByClause = true;
        if (query.offsetClause()) analysis.hasOffsetClause = true;
        if (query.limitClause()) analysis.hasLimitClause = true;

        analysis.isSingleTable = !query.joinClauses()?.length
            && !(analysis.hasAggrFunctions || analysis.hasGroupByClause)
            && !analysis.hasWindowFunctions
            && analysis.hasSubqueryExprs !== 2
            && ((fromItems) => fromItems.length === 1 && !(fromItems[0].expr() instanceof registry.DerivedQuery))(query.fromClause().entries());

        return analysis;
    }

    static intersectQueries(query1, query2, subwindowingRules = null) {

        const specialClauses = [
            // projection
            'select_list',
            // filtering
            'where_clause',
            // windowing
            'order_by_clause',
            'offset_clause',
            'limit_clause'
        ];

        const clauses_a = new Set(query1._keys().filter((c) => !specialClauses.includes(c)));
        const clauses_b = new Set(query2._keys().filter((c) => !specialClauses.includes(c)));
        if (clauses_a.size !== clauses_b.size) return false;
        for (const clauseName of new Set([...clauses_a, ...clauses_b])) {
            if (!clauses_a.has(clauseName) || !clauses_b.has(clauseName)) return false;
            if (!matchExpr(query1._get(clauseName), query2._get(clauseName))) return false;
        }

        const selectMapping = [];
        if (!subwindowingRules || subwindowingRules.projection === '=') {
            const selectItems_a = query1.selectList().entries();
            const selectItems_b = query2.selectList().entries();
            for (const b of selectItems_b) {
                const i_a = selectItems_a.findIndex((a) => matchExpr(a.expr(), b.expr()));
                if (i_a === -1) return false;
                selectMapping.push(i_a);
            }
        }

        const effectiveWhere = [];
        const aWhere = query1.whereClause()?.expr();
        const bWhere = query2.whereClause()?.expr();
        if (aWhere || bWhere) {
            if (subwindowingRules?.whereClause === '>=') {
                if (!aWhere) {
                    effectiveWhere.push(bWhere);
                } else {
                    const _effectiveWhere = matchExpr(aWhere, bWhere, 'AND~');
                    if (_effectiveWhere === false) return false;
                    if (_effectiveWhere === true) effectiveWhere.push(bWhere);
                    else effectiveWhere.push(..._effectiveWhere);
                }
            } else if (!matchExpr(aWhere, bWhere)) return false;
        }

        const aOrd = query1.orderByClause()?.entries() || [];
        const bOrd = query2.orderByClause()?.entries() || [];
        if (!subwindowingRules || subwindowingRules.ordinality === '=') {
            if (aOrd.length !== bOrd.length) return false;
            if (!aOrd.every((a, i) => matchExpr(a.expr(), bOrd[i].expr()))) return false;
            if (!subwindowingRules || subwindowingRules.orderDirections === '=') {
                if (!aOrd.every((a, i) => matchExpr(a.dir(), bOrd[i].dir()))) return false;
            }
        } else if (aOrd.length && !bOrd.length) return false;

        let effectiveOffset = 0;
        const aOffs = query1.offsetClause()?.expr();
        const bOffs = query2.offsetClause()?.expr();
        if (!subwindowingRules || subwindowingRules.offsetClause === '=') {
            if ((aOffs || bOffs) && !matchExpr(aOffs, bOffs)) return;
        } else if (aOffs || bOffs) {
            if (!(bOffs instanceof registry.NumberLiteral)) return false;
            if ((effectiveOffset = bOffs.value() - (aOffs?.value() || 0)) < 0) return false;
        }

        const aLmt = query1.limitClause()?.expr();
        const bLmt = query2.limitClause()?.expr();
        if (!subwindowingRules || subwindowingRules.limitClause === '=') {
            if ((aLmt || bLmt) && !matchExpr(aLmt, bLmt)) return;
        } else if (aLmt || bLmt) {
            if (!(bLmt instanceof registry.NumberLiteral)) return false;
            if (((aLmt?.value() || Infinity) - bLmt.value()) < 0) return false;
        }

        return { selectMapping, filters: effectiveWhere, offset: effectiveOffset };
    }

    // -----------------

    #linkedQlClient;
    #tx;
    #dialect;

    #options;

    #status = 0;
    #lifecycle;

    #exprEngine;
    #queryCtx;

    #query;
    #queryJson;

    #analysis;
    #strategy = {
        ssr: false,
        requeryMode: 'selective',
        requeryWrappedSelectivity: false,
        diffing: 'key',
    };
    #subwindowingRules = {
        projection: '~',
        whereClause: '>=',
        ordinality: '~',
        orderDirections: '~',
        offsetClause: '>=',
        limitClause: '<=',
    };

    #logicalQuery;
    #logicalQueryJson;
    #logicalQueryOptions;

    #originAliases = [];
    #originSchemas = new Map;

    #parentWindow;
    #subwindowConstraints = {
        selectMapping: [],
        filters: [],
        offset: 0,
    };
    #inheritanceDepth = 0;

    #localRecords = new Map;
    #firstRun = false;

    #resolvedOrderElements = [];
    #fromJsonOpts;

    #wal;

    get linkedQlClient() { return this.#linkedQlClient; }
    get tx() { return this.#tx; }
    get dialect() { return this.#dialect; }

    get options() { return this.#options; }
    get query() { return this.#query; }

    get analysis() { return this.#analysis; }
    get strategy() { return this.#strategy; }
    get subwindowingRules() { return this.#subwindowingRules; }

    get status() { return this.#status; }

    get parentWindow() { return this.#parentWindow; }
    get inheritanceDepth() { return this.#inheritanceDepth; }

    get wal() { return this.#wal; }

    constructor(linkedQlClient, query, { tx, noOffsetRevalidate, forceDiffing, ...options } = {}) {
        super();

        if (!(query instanceof registry.BasicSelectStmt)) {
            throw new Error('Only SELECT statements are supported in live mode');
        }
        if (!query.fromClause()) {
            throw new Error('Query has no FROM clause');
        }
        if (!Array.isArray(query.originSchemas())) {
            throw new Error('Expected a pre-resolved query object with originSchemas() returning an array');
        }

        this.#linkedQlClient = linkedQlClient;
        this.#tx = tx;
        this.#dialect = this.#linkedQlClient.dialect;

        this.#query = query;
        this.#queryJson = this.#query.jsonfy({ resultSchemas: false, originSchemas: false });

        // Excluding tx; IMPORTANT
        this.#options = { noOffsetRevalidate, forceDiffing, ...options };
        // Also excluding noOffsetRevalidate, forceDiffing
        this.#logicalQueryOptions = structuredClone(options);
        this.#queryCtx = { options: structuredClone(options) };

        this.#wal = new LinkedQlWal({ drainMode: 'drain', linkedQlClient });

        this.#fromJsonOpts = { dialect: this.#dialect, assert: true };;

        const self = this;
        this.#exprEngine = new ExprEngine(
            async function* (derivedQuery, compositeRow, queryCtx) {
                const stmt = derivedQuery.expr();
                if (!(stmt instanceof registry.SelectStmt)) {
                    throw new Error(`Unexpected expression: ${stmt}`);
                }
                const row = Object.create(null);
                for (const selectItem of stmt.selectList()) {
                    const { alias, value } = await self.#exprEngine.evaluate(selectItem, {}, queryCtx);
                    row[alias] = value;
                }
                yield row;
            },
            this.#options
        );

        if (this.#query.orderByClause()) {
            this.#resolvedOrderElements = this.#exprEngine.resolveScopedRefsInClause(
                this.#query.orderByClause(),
                this.#query.selectList()
            );
        }

        // ------------------ analysis & strategy

        const analysis = this.constructor.analyseQuery(query);
        this.#analysis = analysis;
        const strategy = this.#strategy;

        if (analysis.hasAggrFunctions || analysis.hasGroupByClause) {
            // Can't be computed client-side due to windowing
            strategy.ssr = true; // SELECT: { ssr[, key[, ord]] }
            // Result records not mappable via keys to database records
            strategy.requeryMode = 'wholistic';
            if (this.#options.forceDiffing) {
                strategy.diffing = 'deep'; // key + content
            } else {
                strategy.diffing = false; // no diffing
            }
        }

        if (analysis.hasWindowFunctions) {
            // Can't be computed client-side due to windowing
            strategy.ssr = true; // SELECT: { ssr[, key[, ord]] }
            if (strategy.requeryMode === 'selective') {
                // Wrapped; to happen only after windowing
                strategy.requeryWrappedSelectivity = true;
            }
            if (strategy.diffing !== false) {
                // if not already disabled by aggr functions
                strategy.diffing = 'deep'; // key + content
            }
        }

        if (analysis.hasSubqueryExprsInSelect
            || analysis.hasSubqueryExprsInOrderBy) {
            // Can't be computed client-side
            strategy.ssr = true; // SELECT: { ssr[, key[, ord]] }
            if (strategy.diffing !== false
                && analysis.hasSubqueryExprsInSelect) {
                // if not already disabled by aggr functions
                strategy.diffing = 'deep'; // key + content
            }
        }

        if (analysis.hasOffsetClause
            && !this.#options.noOffsetRevalidate) {
            strategy.requeryMode = 'wholistic';
        }

        if (analysis.hasOffsetClause
            || (analysis.hasLimitClause && analysis.hasOrderByClause)) {
            // Can't be computed client-side due to windowing
            if (strategy.requeryMode === 'selective') {
                // Wrapped; to happen only after windowing
                strategy.requeryWrappedSelectivity = true;
            }
        }

        // ------------------ subwindowingRules

        if (strategy.ssr) {
            // RESULT shape doesn't support client-side computing of projection & WHERE & ORDER BY
            // so these must match
            this.#subwindowingRules.projection = '=';
            this.#subwindowingRules.whereClause = '=';
            this.#subwindowingRules.ordinality = '=';
        } else {
            // RESULT shape supports client-side computing of projection & ORDER BY
            // but subqueries in WHERE can't be computed client-side, so, WHERE must match
            if (analysis.hasSubqueryExprsInWhere) {
                this.#subwindowingRules.whereClause = '=';
            }
        }

        if (analysis.hasOffsetClause
            || (analysis.hasLimitClause && analysis.hasOrderByClause)) {
            // Has OFFSET/LIMIT + ORDER BY (windowing):
            // so everything must match including WHERE & ORDER BY (expressions + directions) due to windowing
            this.#subwindowingRules.whereClause = '=';
            this.#subwindowingRules.ordinality = '=';
            this.#subwindowingRules.orderDirections = '=';
            // - OFFSET/LIMIT may differ in subwindow but must fall within current window range
            // - projection may differ in subwindow
        }
    }

    async inherit(parentWindow) {
        // Reset inheritance
        if (parentWindow === null) {
            this.#subwindowConstraints = {
                selectMapping: [],
                filters: [],
                offset: 0,
            };
            this.#inheritanceDepth = 0;
            if (this.#parentWindow) {
                this.#parentWindow = null;
                await this.stop();
            }
            return;
        }
        // Process intersection
        if (!(parentWindow instanceof QueryWindow)) {
            throw new Error(`Parent window must be instance of QueryWindow or null`);
        }

        // Already have been filtered by RealtimeClient tho
        if (this.#tx !== parentWindow.#tx) return false;
        // Comparison excludes tx, and id - as those are excluded from options
        if (!_eq(this.#options, parentWindow.#options)) return false;

        if (!_eq(this.#subwindowingRules, parentWindow.#subwindowingRules)) return false;
        const result = this.constructor.intersectQueries(
            parentWindow.#query,
            this.#query,
            this.#subwindowingRules
        );
        if (result === false) return false;
        // Success...
        this.#subwindowConstraints = result;
        this.#inheritanceDepth = parentWindow.inheritanceDepth + 1;
        this.#parentWindow = parentWindow;
        await this.start();
        return true;
    }

    // -------------

    async start() {
        await this.stop();
        if (this.#parentWindow) {
            await this.#initializeAsSub();
        } else await this.#initializeAsRoot();
        this.#status = 1;
    }

    async stop() {
        await this.#lifecycle?.abort();
        this.#lifecycle = null;
        this.#status = 0;
        await this.#wal.close({ destroy: true });
    }

    // -------------

    async #initializeAsRoot() {
        const analysis = this.#analysis;
        const strategy = this.#strategy;

        // Construct FromItem schemas
        // off analysis.fromItemsByAlias & query.originSchemas()
        const getJson = (node) => {
            const value = node.value();
            const delim = node._get('delim');
            return { value: delim ? value : value.toLowerCase(), delim };
        };
        for (const [alias, relationHashes] of Object.entries(analysis.fromItemsByAlias)) {
            const originSchema = this.#query.originSchemas().find((os) => {
                if (os instanceof registry.JSONSchema) return alias === '';
                return os.identifiesAs(alias);
            });
            let $columns,
                $keyColumns,
                usingAllColumnsForKeyColumns = false;
            if (originSchema instanceof registry.JSONSchema) {
                $columns = originSchema.columns().map((e) => getJson(e.name()));
                if (relationHashes.size > 1 || !($keyColumns = originSchema.entries().filter((e) => e.pkConstraint()).map(getJson)).length) {
                    $keyColumns = structuredClone($columns);
                    usingAllColumnsForKeyColumns = true;
                }
            } else {
                $columns = originSchema.columns().map((e) => getJson(e.name()));
                if (relationHashes.size > 1 || !($keyColumns = originSchema.pkConstraint(true)?.columns().map(getJson))) {
                    $keyColumns = structuredClone($columns);
                    usingAllColumnsForKeyColumns = true;
                }
            }
            const relation = relationHashes.size === 1
                ? (([namespace, name]) => ({ namespace, name }))(JSON.parse([...relationHashes][0]))
                : null;
            this.#originSchemas.set(alias, {
                relation,
                relationHashes: relationHashes,
                columns: $columns.map((c) => c.value),
                keyColumns: $keyColumns.map((c) => c.value),
                usingAllColumnsForKeyColumns,
                $columns,
                $keyColumns,
            });
            const delim = alias !== alias.toLowerCase()
                || /^\d/.test(alias)
                || !/^(\*|[\w]+)$/.test(alias);
            this.#originAliases.push({ value: alias, delim: delim && '"' || '' });
        }

        // ----------- newQueryHead

        // Generate a new head for the query
        // This is for internal computation
        const newOrderElementsJson = this.#resolvedOrderElements.map((oi) => oi.jsonfy());
        let newQueryHead = this.#originAliases.reduce((acc, aliasJson) => {
            const originSchema = this.#originSchemas.get(aliasJson.value);
            const XMIN_autoInjection = this.#dialect !== 'mysql' && !originSchema.$columns.find((c) => c.value.toUpperCase() === 'XMIN');

            // Column key/value construction
            const createColKeyValJson = (colJson) => {
                const keyJson = { nodeName: 'STRING_LITERAL', ...colJson };
                let colRefJson = { nodeName: 'COLUMN_REF1', ...colJson, qualifier: { nodeName: 'TABLE_REF1', ...aliasJson } };
                if (colJson.value === 'XMIN' && XMIN_autoInjection) {
                    colRefJson = { nodeName: 'CAST_EXPR', expr: colRefJson, data_type: { nodeName: 'DATA_TYPE', value: 'TEXT' } };
                }
                // Format for strategy.aggrMode === 2?
                if (analysis.hasAggrFunctions) {
                    const fnName = this.#dialect === 'mysql' ? 'JSON_STRINGAGG' : 'JSON_AGG';
                    colRefJson = { nodeName: 'CALL_EXPR', name: fnName, arguments: [colRefJson] };
                }
                return [keyJson, colRefJson];
            };
            // Compose the cols JSON
            const fnName = this.#dialect === 'mysql' ? 'JSON_OBJECT' : 'JSON_BUILD_OBJECT';
            const fnArgs = (
                strategy.ssr
                    ? originSchema.$keyColumns
                    : originSchema.$columns.concat(XMIN_autoInjection ? { value: 'XMIN' } : [])
            ).reduce((colKeyValJsons, colJson) => ([...colKeyValJsons, ...createColKeyValJson(colJson)]), []);
            const aliasColsExpr = { nodeName: 'CALL_EXPR', name: fnName, arguments: fnArgs };

            // Format for strategy.ssr?
            // SELECT: { ssr: {...}, key: {...}[, ord] }
            if (strategy.ssr) {
                const aliasKeyJson = { nodeName: 'STRING_LITERAL', ...aliasJson };
                return acc.concat(aliasKeyJson, aliasColsExpr);
            }

            // Format for nornal select list
            // SELECT: { t1: {...}, t2: {...}, }
            const aliasKeyJson = { nodeName: 'SELECT_ITEM_ALIAS', ...aliasJson };
            return acc.concat({
                nodeName: 'SELECT_ITEM',
                alias: aliasKeyJson,
                expr: aliasColsExpr,
            });
        }, []);

        // Format for strategy.ssr?
        // SELECT: { ssr: {...}, key: {...}[, ord] }
        if (strategy.ssr) {
            const fnName = this.#dialect === 'mysql' ? 'JSON_OBJECT' : 'JSON_BUILD_OBJECT';
            // 1. Whole original query head as a select item
            const originalsArgs = this.#queryJson.select_list.entries.reduce((acc, si) => {
                return acc.concat({ nodeName: 'STRING_LITERAL', value: si.alias.value }, si.expr);
            }, []);
            const originalsJson = { nodeName: 'CALL_EXPR', name: fnName, arguments: originalsArgs };
            // 2. All keys as a select item
            const keysJson = { nodeName: 'CALL_EXPR', name: fnName, arguments: newQueryHead };
            // Final newQueryHead
            newQueryHead = [{ nodeName: 'SELECT_ITEM', alias: { nodeName: 'SELECT_ITEM_ALIAS', value: 'ssr' }, expr: originalsJson }];
            if (strategy.diffing) {
                newQueryHead.push({ nodeName: 'SELECT_ITEM', alias: { nodeName: 'SELECT_ITEM_ALIAS', value: 'key' }, expr: keysJson });
                // Oh ... with ordinality?
                if (analysis.hasOrderByClause) {
                    const fnName = this.#dialect === 'mysql' ? 'JSON_ARRAY' : 'JSON_BUILD_ARRAY';
                    const ordsJson = { nodeName: 'CALL_EXPR', name: fnName, entries: newOrderElementsJson.map((oi) => oi.expr) };
                    newQueryHead.push({ nodeName: 'SELECT_ITEM', alias: { nodeName: 'SELECT_ITEM_ALIAS', value: 'ord' }, expr: ordsJson });
                }
            }
        }

        // Declare this.#logicalQueryJson
        // SELECT: { t1: {...}[, t2: {...}[, ...]] }
        const select_list = { entries: newQueryHead };
        const order_by_clause = newOrderElementsJson.length ? { entries: newOrderElementsJson } : undefined;
        this.#logicalQueryJson = { ...this.#queryJson, select_list, order_by_clause };
        this.#logicalQuery = this.#query.constructor.fromJSON(this.#logicalQueryJson, this.#fromJsonOpts);

        // Normalize query params
        if (!strategy.ssr) {
            this.#logicalQueryOptions.values = [];

            const inputQueryParams = this.#options.values;
            const expectedLength = inputQueryParams?.length || 0;

            let bindingIndex = 1;

            const transformer = new Transformer((node, defaultTransform) => {
                if (node instanceof registry.BindVar) {
                    const i = node.value();
                    if (i > expectedLength) {
                        throw new SyntaxError(`Could not determine data type of parameter ${node}`);
                    }
                    this.#logicalQueryOptions.values.push(
                        inputQueryParams[i - 1]
                    );
                    return { ...node.jsonfy(), value: bindingIndex++ };
                }
                // For all other things...
                return defaultTransform();
            });
            this.#logicalQuery = this.#logicalQuery.clone({}, transformer);
        }

        // ----------- connect

        // Connect to WAL events or equivalent
        // Drivers must implement the interface
        this.#lifecycle = await this.#linkedQlClient.wal.subscribe(analysis.fromItemsBySchema, (commit) => {
            this.#handleCommit(commit).catch((e) => {
                this.emit('error', e);
            });
        }, { tx: this.#tx, liveQueryOriginated: SYSTEM_TAG, isSingleTableLiveQuery: analysis.isSingleTable });
        this.#lifecycle.on('error', (e) => {
            if (this.#status !== 0) this.emit('error', e);
        });
    }

    async #initializeAsSub() {
        // Handle "rawresult"
        const abortLine1 = this.#parentWindow.on('rawresult', async ({ entries: resultRecords, ...commitMeta }) => {
            resultRecords = await this.#applySubwindowConstraints(resultRecords);
            await this.#commitResult(commitMeta, resultRecords, true);
        });

        // Handle "rawdiff"
        const abortLine2 = this.#parentWindow.on('rawdiff', async ({ entries: diffEvents, ...commitMeta }) => {
            const _diffEvents = new Set;

            for (let diffEvent of diffEvents) {
                if (diffEvent.op === 'delete') {
                    // Ignore if not in this window
                    if (!this.#localRecords.has(diffEvent.oldHash)) continue;
                } else {
                    const passesWhere = await this.#applySubwindowWheres(diffEvent.logicalRecord);
                    if (this.#localRecords.has(diffEvent.oldHash)) {
                        diffEvent = !passesWhere
                            ? { ...diffEvent, op: 'delete' } // drop
                            : { ...diffEvent }; // keep
                        _diffEvents.add(diffEvent);
                    } else if (passesWhere) {
                        // Cast to insert; even if update
                        diffEvent = { ...diffEvent, op: 'insert' };
                        _diffEvents.add(diffEvent);
                    }
                }
            }
            if (_diffEvents.size) await this.#commitDiffs(commitMeta, _diffEvents);
        });

        // Set abortLine
        this.#lifecycle = {
            abort: () => {
                abortLine1();
                abortLine2();
            }
        };
    }

    // -------------

    async #applySubwindowConstraints(resultRecords) {
        const analysis = this.#analysis;
        const strategy = this.#strategy;

        let resultEntries = [];
        for (const [logicalHash, logicalRecord] of resultRecords) {
            if (await this.#applySubwindowWheres(logicalRecord)) {
                resultEntries.push([logicalHash, logicalRecord]);
            }
        }
        if (strategy.diffing && analysis.hasOrderByClause && (
            this.#subwindowingRules.ordinality === '~' || this.#subwindowingRules.orderDirections === '~')) {
            resultEntries = await this.#applySorting(resultEntries);
        }
        if (analysis.hasOffsetClause || analysis.hasLimitClause) {
            const effectiveLimit = await this.#exprEngine.evaluate(this.#query.limitClause().expr(), {}, this.#queryCtx);
            resultEntries = resultEntries.slice(this.#subwindowConstraints.offset, effectiveLimit);
        }
        return resultEntries;
    }

    async #applySubwindowWheres(logicalRecord) {
        for (const expr of this.#subwindowConstraints.filters) {
            const _eval = await this.#exprEngine.evaluate(expr, logicalRecord, this.#queryCtx);
            if (!_eval) return false;
        }
        return true;
    }

    // -------------

    async currentRendering() {
        const resultEntries = await this.currentRecords();
        const rows = [], hashes = [];
        for (const [logicalHash, logicalRecord] of resultEntries) {
            const row = await this.#renderLogicalRecord(logicalRecord);
            rows.push(row);
            hashes.push(logicalHash);
        }
        return { rows, hashes };
    }

    async currentRecords() {
        if (!this.#firstRun) {
            let resultEntries;
            if (this.#parentWindow) {
                resultEntries = await this.#parentWindow.currentRecords();
                resultEntries = await this.#applySubwindowConstraints(resultEntries);
            } else {
                resultEntries = await this.#queryHeadless();
            }
            const commitMeta = { commitTime: 0, computed: true };
            await this.#commitResult(commitMeta, resultEntries);
            this.#firstRun = true;
        }
        return [...this.#localRecords];
    }

    async #queryHeadless(extraWhere = null) {
        const strategy = this.#strategy;
        let logicalQuery = this.#logicalQuery;

        if (extraWhere && strategy.requeryWrappedSelectivity) {
            const deriveSelectItem = (si) => ({ ...si, expr: { ...si.expr, qualifier: undefined } });
            const logicalQueryJson = {
                nodeName: 'BASIC_SELECT_STMT',
                select_list: { entries: this.#logicalQueryJson.select_list.entries.map(deriveSelectItem) },
                from_clause: { entries: [{ nodeName: 'FROM_ITEM', expr: { nodeName: 'DERIVED_QUERY', expr: this.#logicalQueryJson } }] },
                where_clause: { expr: extraWhere },
            };
            logicalQuery = this.#query.constructor.fromJSON(logicalQueryJson, this.#fromJsonOpts);
        } else if (extraWhere) {
            let logicalQueryJson = this.#logicalQueryJson;
            const finalWhereJson = logicalQueryJson.where_clause?.expr
                ? { nodeName: 'BINARY_EXPR', left: extraWhere, operator: 'AND', right: logicalQueryJson.where_clause.expr }
                : extraWhere;
            logicalQueryJson = {
                ...logicalQueryJson,
                where_clause: { nodeName: 'WHERE_CLAUSE', expr: finalWhereJson },
            };
            logicalQuery = this.#query.constructor.fromJSON(logicalQueryJson, this.#fromJsonOpts);
        }

        // With tx; IMPORTANT
        const result = await this.#linkedQlClient.query(logicalQuery, { tx: this.#tx, ...this.#logicalQueryOptions, liveQueryOriginated: SYSTEM_TAG });

        const resultEntries = [];
        for (const [i, logicalRecord] of result.rows.entries()) {
            const logicalHash = strategy.diffing
                ? this.#deriveLogicalHash(logicalRecord)
                : `$${i}`;
            resultEntries.push([logicalHash, logicalRecord]);
        }

        return new Map(resultEntries);
    }

    // -------------

    #getKeyValue(logicalRecord, alias, k) {
        return this.#strategy.ssr
            ? logicalRecord.key[alias][k]
            : logicalRecord[alias][k];
    }

    #deriveLogicalHash(logicalRecord) {
        const newKeysList = [...this.#originSchemas.entries()].map(([alias, originSchema]) => {
            const _newKeys = originSchema.keyColumns.map((k) => this.#getKeyValue(logicalRecord, alias, k));
            if (_newKeys.every((s) => s === null)) {
                return null; // IMPORTANT
            }
            return _newKeys;
        });
        return this.#stringifyLogicalHash(newKeysList);
    }

    #stringifyLogicalHash(keyValues) {
        return JSON.stringify(keyValues);
    }

    #parseLogicalHash(logicalHash) {
        return JSON.parse(logicalHash);
    }

    #localSet(logicalHash, logicalRecord) {
        this.#localRecords.set(logicalHash, logicalRecord);
    }

    #localDelete(logicalHash) {
        this.#localRecords.delete(logicalHash);
    }

    #localReindex(idChanges) {
        this.#localRecords = new Map([...this.#localRecords.entries()].map(([id, logicalRecord]) => {
            if (idChanges.has(id)) id = idChanges.get(id);
            return [id, logicalRecord];
        }));
    }

    async #renderLogicalRecord(logicalRecord) {
        const row = Object.create(null);

        if (this.#strategy.ssr) {
            if (!this.#subwindowConstraints.filters.length) return logicalRecord.ssr;
            const renderedValues = Object.values(logicalRecord.ssr);
            for (const [i, si] of this.#query.selectList().entries().entries()) {
                const value = renderedValues[i];
                const alias = si.alias()?.value() || '?column?';
                row[alias] = value;
            }
            return row;
        }

        for (const selectItem of this.#query.selectList()) {
            const { alias, value } = await this.#exprEngine.evaluate(selectItem, logicalRecord, this.#queryCtx);
            row[alias] = value;
        }
        return row;
    }

    // -------------

    #normalizeEvents({ txId, entries: events }) {
        const normalizedEventsMap = new Map;
        const keyHistoryMap = new Map;
        const allAffectedAliases = new Set;

        for (const e of events) {
            if (!(e.op === 'insert' || e.op === 'update' || e.op === 'delete')
                || !e.relation?.keyColumns) continue;

            const relationHash = JSON.stringify([e.relation.namespace, e.relation.name]);

            const affectedAliasesEntries = [...this.#originSchemas.entries()].filter(([, originSchema]) => originSchema.relationHashes.has(relationHash));

            if (!affectedAliasesEntries.length
                || affectedAliasesEntries.find(([, originSchema]) => originSchema.relationHashes.size > 1)) {
                return true;
            }

            const affectedAliases = affectedAliasesEntries.map(([alias]) => alias);
            for (const alias of affectedAliases) allAffectedAliases.add(alias);

            const keyColumns = e.relation.keyColumns;
            const oldKey = e.key
                // update|delete with REPLICA IDENTITY DEFAULT
                ? Object.values(e.key)
                // (update|delete with REPLICA IDENTITY FULL) | insert
                : keyColumns.map((k) => (e.old || e.new)[k]);
            const newKey = e.new || e.old
                // (update|delete with REPLICA IDENTITY FULL) | insert
                ? keyColumns.map((k) => (e.new || e.old)[k])
                // update|delete with REPLICA IDENTITY DEFAULT
                : Object.values(e.key);

            let eWithXMIN = e;
            if (this.#dialect !== 'mysql' && e.new/* INSERT|UPDATE */ && !(e.new.xmin || e.new.XMIN)) {
                eWithXMIN = { ...e, new: { ...e.new, XMIN: txId } };
            }

            const normalizedEvent = { ...eWithXMIN, keyColumns, oldKey, newKey, relationHash, affectedAliases };

            let rowKeyHash_old = this.#stringifyLogicalHash([e.relation.namespace, e.relation.name, normalizedEvent.oldKey]);
            let rowKeyHash_new, previous;

            if ((previous = normalizedEventsMap.get(rowKeyHash_old))
                // Or if previous op was an update with key change... we see if rowKeyHash_old === previos' rowKeyHash_new
                || keyHistoryMap.has(rowKeyHash_old) && (previous = normalizedEventsMap.get(rowKeyHash_old = keyHistoryMap.get(rowKeyHash_old)))) {
                if (previous.op === 'insert' && normalizedEvent.op === 'delete') {
                    // Ignore; inconsequential
                    continue;
                }
                if (previous.op === 'delete' && normalizedEvent.op === 'insert') {
                    // Treat as update should in case props were changed before reinsertion
                    const _normalizedEvent = { ...normalizedEvent, op: 'update', old: previous.old };
                    normalizedEventsMap.set(rowKeyHash_old, _normalizedEvent);
                    continue;
                }
                if (previous.op === 'insert' && normalizedEvent.op === 'update') {
                    // Use the lastest state of said record for the insert
                    const _normalizedEvent = { ...normalizedEvent, oldKey: previous.oldKey, old: null, op: 'insert' };
                    normalizedEventsMap.delete(rowKeyHash_old); // Don't retain old slot; must come first
                    normalizedEventsMap.set(rowKeyHash_old, _normalizedEvent);
                    // Handle key changes
                    if ((rowKeyHash_new = this.#stringifyLogicalHash([e.relation.namespace, e.relation.name, normalizedEvent.newKey])) !== rowKeyHash_old) {
                        keyHistoryMap.set(rowKeyHash_new, rowKeyHash_old);
                    }
                    continue;
                }
                if (previous.op === 'update' && normalizedEvent.op === 'update') {
                    // Honour latest update, but mapped to old identity
                    const _normalizedEvent = { ...normalizedEvent, oldKey: previous.oldKey, old: previous.old };
                    normalizedEventsMap.delete(rowKeyHash_old); // Don't retain old slot; must come first
                    normalizedEventsMap.set(rowKeyHash_old, _normalizedEvent);
                    // Handle key changes
                    if ((rowKeyHash_new = this.#stringifyLogicalHash([e.relation.namespace, e.relation.name, normalizedEvent.newKey])) !== rowKeyHash_old) {
                        keyHistoryMap.set(rowKeyHash_new, rowKeyHash_old);
                    }
                    continue;
                }
                if (previous.op === 'update' && normalizedEvent.op === 'delete') {
                    // Honour latest event using same ID
                    normalizedEventsMap.delete(rowKeyHash_old); // Don't retain old slot
                    normalizedEventsMap.set(rowKeyHash_old, normalizedEvent);
                    continue;
                }
            } else {
                if (normalizedEvent.op === 'update') {
                    // Handle key changes
                    if ((rowKeyHash_new = this.#stringifyLogicalHash([e.relation.namespace, e.relation.name, normalizedEvent.newKey])) !== rowKeyHash_old) {
                        keyHistoryMap.set(rowKeyHash_new, rowKeyHash_old);
                    }
                }
                normalizedEventsMap.set(rowKeyHash_old, normalizedEvent);
            }
        }

        return [normalizedEventsMap, keyHistoryMap, allAffectedAliases];
    }

    async #handleCommit(commit) {
        if (commit.computed) {
            throw new Error('Table-level commits expected but computed commit received');
        }
        const analysis = this.#analysis;
        const strategy = this.#strategy;
        const commitMeta = { ...commit, computed: true, entries: null };
        if (strategy.requeryMode === 'wholistic') {
            // Aggr functions in the house
            return await this.#diffWithOrigin_Wholistic(commitMeta);
        }
        const normalizeEvents = this.#normalizeEvents(commit);
        if (normalizeEvents === true) {
            return await this.#diffWithOrigin_Wholistic(commitMeta);
        }
        if (analysis.isSingleTable && (!this.#tx || commit.isVisible === SYSTEM_TAG)) {
            const [normalizedEventsMap] = normalizeEvents;
            return await this.#diffWithLocal(commitMeta, normalizedEventsMap);
        }
        const [normalizedEventsMap, keyHistoryMap, allAffectedAliases] = normalizeEvents;
        return await this.#diffWithOrigin_Selective(commitMeta, normalizedEventsMap, keyHistoryMap, allAffectedAliases);
    }

    async #diffWithLocal(commitMeta, normalizedEventsMap) {
        const diffEvents = new Set;

        for (const normalizedEvent of normalizedEventsMap.values()) {

            if (normalizedEvent.op === 'insert') {
                const newHash = this.#stringifyLogicalHash([normalizedEvent.newKey]);
                const logicalRecord = { [normalizedEvent.affectedAliases[0]]: normalizedEvent.new };
                const passesWhere = !this.#query.whereClause()
                    || await this.#exprEngine.evaluate(this.#query.whereClause().expr(), logicalRecord, this.#queryCtx);
                if (!passesWhere) continue;
                const diffEvent = { op: 'insert', newHash, logicalRecord };
                diffEvents.add(diffEvent);
            }

            if (normalizedEvent.op === 'update') {
                const oldHash = this.#stringifyLogicalHash([normalizedEvent.oldKey]);
                const newHash = this.#stringifyLogicalHash([normalizedEvent.newKey]);
                const logicalRecord = { [normalizedEvent.affectedAliases[0]]: normalizedEvent.new };
                let diffEvent;
                const passesWhere = !this.#query.whereClause()
                    || await this.#exprEngine.evaluate(this.#query.whereClause().expr(), logicalRecord, this.#queryCtx);
                if (this.#localRecords.has(oldHash)) {
                    diffEvent = !passesWhere
                        ? { op: 'delete', oldHash }
                        : { op: 'update', oldHash, newHash, logicalRecord };
                } else if (passesWhere) {
                    diffEvent = { op: 'insert', newHash, logicalRecord };
                }
                if (diffEvent) diffEvents.add(diffEvent);
            }

            if (normalizedEvent.op === 'delete') {
                const oldHash = this.#stringifyLogicalHash([normalizedEvent.oldKey]);
                if (this.#localRecords.has(oldHash)) {
                    const diffEvent = { op: 'delete', oldHash, logicalRecord: this.#localRecords.get(oldHash) };
                    diffEvents.add(diffEvent);
                }
            }
        }

        return await this.#commitDiffs(commitMeta, diffEvents);
    }

    async #diffWithOrigin_Selective(commitMeta, normalizedEventsMap, keyHistoryMap, allAffectedAliases) {
        const strategy = this.#strategy;
        const analysis = this.#analysis;

        const composeSelectionLogic = (alias, keyColumns, keyValues, nullTest = 0) => {
            if (keyColumns.length > 1) {
                const operands = keyColumns.map((keyColumn, i) => composeSelectionLogic(alias, [keyColumn], [keyValues[i]], nullTest));
                return operands.reduce((left, right) => registry.BinaryExpr.fromJSON({
                    nodeName: 'BINARY_EXPR',
                    left,
                    operator: 'AND',
                    right,
                }, this.#fromJsonOpts), operands.shift());
            }
            // Compose...
            const columnRef = strategy.requeryWrappedSelectivity
                // key -> alias -> keyColumn
                ? { nodeName: 'BINARY_EXPR', left: { nodeName: 'COLUMN_REF1', value: 'key' }, operator: '->', right: { nodeName: 'BINARY_EXPR', left: { nodeName: 'COLUMN_REF1', value: alias }, operator: '->>', right: { nodeName: 'COLUMN_REF1', value: keyColumns[0] } } }
                // alias.keyColumn
                : { nodeName: 'COLUMN_REF1', value: keyColumns[0], qualifier: { nodeName: 'TABLE_REF1', value: alias } };
            // Compose: <keyColumn> IS NULL
            const nullLiteral = { nodeName: 'NULL_LITERAL', value: 'NULL' };
            const isNullExpr = registry.BinaryExpr.fromJSON({
                nodeName: 'BINARY_EXPR',
                left: columnRef,
                operator: 'IS',
                right: nullLiteral,
            }, this.#fromJsonOpts);
            if (nullTest === 0) {
                return isNullExpr;
            }
            // Compose: <keyColumn> = <keyValue>
            const valueLiteral = { nodeName: typeof keyValues[0] === 'number' ? 'NUMBER_LITERAL' : 'STRING_LITERAL', value: keyValues[0] };
            const eqExpr = registry.BinaryExpr.fromJSON({
                nodeName: 'BINARY_EXPR',
                left: columnRef,
                operator: '=',
                right: valueLiteral
            }, this.#fromJsonOpts);
            if (analysis.isSingleTable) {
                return eqExpr;
            }
            // Compose?: (<keyColumn> IS NULL OR <keyColumn> = <keyValue>)
            if (nullTest === 2) {
                const orExpr = {
                    nodeName: 'BINARY_EXPR',
                    left: isNullExpr,
                    operator: 'OR',
                    right: eqExpr,
                };
                return registry.RowConstructor.fromJSON({ nodeName: 'ROW_CONSTRUCTOR', entries: [orExpr] }, this.#fromJsonOpts);
            }
            return eqExpr;
        };

        // ----------------

        const localRecords = new Map;
        const remoteDiffingFilters = [];

        for (const normalizedEvent of normalizedEventsMap.values()) {
            let diffingFilters = [];
            const affectedAliases = normalizedEvent.affectedAliases;
            if (normalizedEvent.op === 'insert') {
                // keyColumn === null // keyColumn IN [null, newKey]
                diffingFilters = [
                    affectedAliases.map((alias) => composeSelectionLogic(alias, normalizedEvent.keyColumns, normalizedEvent.oldKey, 0)),
                    affectedAliases.map((alias) => composeSelectionLogic(alias, normalizedEvent.keyColumns, normalizedEvent.newKey, 2/* IMPORTANT */)),
                ];
            }
            if (normalizedEvent.op === 'update') {
                // keyColumn IN [null, oldKey] // keyColumn IN [null, newKey]
                diffingFilters = [
                    affectedAliases.map((alias) => composeSelectionLogic(alias, normalizedEvent.keyColumns, normalizedEvent.oldKey, 2)),
                    affectedAliases.map((alias) => composeSelectionLogic(alias, normalizedEvent.keyColumns, normalizedEvent.newKey, 2)),
                ];
            }
            if (normalizedEvent.op === 'delete') {
                // keyColumn IN [null, oldKey] // keyColumn === null
                diffingFilters = [
                    affectedAliases.map((alias) => composeSelectionLogic(alias, normalizedEvent.keyColumns, normalizedEvent.oldKey, 2/* IMPORTANT */)),
                    affectedAliases.map((alias) => composeSelectionLogic(alias, normalizedEvent.keyColumns, normalizedEvent.newKey, 0)),
                ];
            }
            top: for (const [logicalHash, logicalRecord] of this.#localRecords.entries()) {
                for (const expr of diffingFilters[0]) {
                    if (!await this.#exprEngine.evaluate(expr, logicalRecord, this.#queryCtx)) {
                        continue top;
                    }
                }
                // All of diffingFilters[0] matched - AND
                localRecords.set(logicalHash, logicalRecord);
            }
            // Build this event's diffingFilters
            const diffingFilters_currentEvent = diffingFilters[1].reduce((left, right) => {
                return registry.BinaryExpr.fromJSON({ nodeName: 'BINARY_EXPR', left, operator: 'AND', right });
            }, diffingFilters[1].shift());
            remoteDiffingFilters.push(diffingFilters_currentEvent);
        }

        let diffingFilters_allEvents = remoteDiffingFilters[0];
        if (remoteDiffingFilters.length > 1) {
            // Compose the logic
            const _diffingFilters_allEvents = remoteDiffingFilters.reduce((left, right) => {
                return registry.BinaryExpr.fromJSON({ nodeName: 'BINARY_EXPR', left, operator: 'OR', right });
            }, remoteDiffingFilters.shift());
            // Wrap the logic?
            if (!strategy.requeryWrappedSelectivity) {
                diffingFilters_allEvents = registry.RowConstructor.fromJSON({
                    nodeName: 'ROW_CONSTRUCTOR',
                    entries: [_diffingFilters_allEvents],
                });
            } else diffingFilters_allEvents = _diffingFilters_allEvents;
        }

        // ----------------

        const resolveTransition = (oldHash, matches) => {
            const oldHash_parsed = this.#parseLogicalHash(oldHash);
            let i;
            for (const [alias, originSchema] of this.#originSchemas.entries()) {
                if (allAffectedAliases.has(alias)) {
                    const relation = originSchema.relation;
                    // An event happen of this alias?
                    let possibleEventId;
                    let normalizedEvent;
                    if (oldHash_parsed[i] === null) {
                        // Find INSERTS or UPDATES that might slot in here from the right hand side
                        matches = matches.filter(([k]) => {
                            // Find an INSERT or UPDATE that might talk about this object
                            if (k[i] !== null && (possibleEventId = JSON.stringify([relation.namespace, relation.name, k[i]])) && (
                                (normalizedEvent = normalizedEventsMap.get(possibleEventId))
                                || keyHistoryMap.has(possibleEventId) && (normalizedEvent = normalizedEventsMap.get(possibleEventId = keyHistoryMap.get(possibleEventId)))
                            )) return true; // At least an INSERT or UPDATE is found on oldHash_parsed[i]
                        });
                    } else {
                        // Find a DELETE or UPDATE that might talk about this object
                        if ((possibleEventId = JSON.stringify([relation.namespace, relation.name, oldHash_parsed[i]]))
                            && (normalizedEvent = normalizedEventsMap.get(possibleEventId))) {
                            matches = matches.filter(([k]) => {
                                // Remote hash would be null on normalizedEvent.op === 'delete'
                                return normalizedEvent.op === 'delete' ? k[i] === null : _eq(normalizedEvent.newKey, k[i]);
                            });
                        }
                    }
                }
                // Default matcher: exact key values at given slot
                matches = matches.filter(([k]) => _eq(oldHash_parsed[i], k[i]));
                i++;
            }
            if (!matches.length) return [];
            return [this.#stringifyLogicalHash(matches[0][0]), matches[0][1]];
        };

        // ----------------

        const remoteRecords = await this.#queryHeadless(diffingFilters_allEvents);
        return await this.#createDiffs(commitMeta, localRecords, remoteRecords, resolveTransition);
    }

    async #diffWithOrigin_Wholistic(commitMeta) {
        const remoteRecords = await this.#queryHeadless();
        return !this.#strategy.diffing
            ? await this.#commitResult(commitMeta, remoteRecords, true)
            : await this.#createDiffs(commitMeta, this.#localRecords, remoteRecords);
    }

    async #createDiffs(commitMeta, localRecords, remoteRecords, resolveTransition = null) {
        const diffEvents = new Set;

        const parseRemoteRecords = () => [...remoteRecords.entries()].map(([k, v]) => [this.#parseLogicalHash(k), v]);
        let remoteRecords_parsed = parseRemoteRecords();

        for (const [logicalHash_existing, logicalRecord_existing] of localRecords.entries()) {
            if (remoteRecords.has(logicalHash_existing)) {
                const logicalRecord_new = remoteRecords.get(logicalHash_existing);
                remoteRecords.delete(logicalHash_existing);
                remoteRecords_parsed = parseRemoteRecords(); // refresh
                const diffEvent = { op: 'update', oldHash: logicalHash_existing, newHash: logicalHash_existing, logicalRecord: logicalRecord_new };
                diffEvents.add(diffEvent);
                continue;
            }
            if (resolveTransition) {
                const [logicalHash_new, logicalRecord_new] = resolveTransition(logicalHash_existing, remoteRecords_parsed);
                if (logicalHash_new) {
                    remoteRecords.delete(logicalHash_new);
                    remoteRecords_parsed = parseRemoteRecords(); // refresh
                    const diffEvent = { op: 'update', oldHash: logicalHash_existing, newHash: logicalHash_new, logicalRecord: logicalRecord_new };
                    diffEvents.add(diffEvent);
                    continue;
                }
            }
            const diffEvent = { op: 'delete', oldHash: logicalHash_existing, logicalRecord: logicalRecord_existing };
            diffEvents.add(diffEvent);
        }

        for (const [logicalHash_new, logicalRecord_new] of remoteRecords.entries()) {
            const diffEvent = { op: 'insert', newHash: logicalHash_new, logicalRecord: logicalRecord_new };
            diffEvents.add(diffEvent);
        }

        return await this.#commitDiffs(commitMeta, diffEvents);
    }

    async #commitDiffs(commitMeta, diffEvents) {
        if (!diffEvents?.size) return false;
        const analysis = this.#analysis;
        const _diffEvents = new Map;
        const outputEvents = new Map;
        const idChanges = new Map;
        const deferredInserts = new Set;

        const effectiveLimit = analysis.hasLimitClause
            ? await this.#exprEngine.evaluate(this.#query.limitClause().expr(), {}, this.#queryCtx)
            : 0;

        const render = async (diffEvent) => {
            const newRow = await this.#renderLogicalRecord(diffEvent.logicalRecord);
            const outputEvent = {
                op: diffEvent.op,
                ...(diffEvent.op === 'update' ? { oldHash: diffEvent.oldHash, old: diffEvent.old } : {}),
                newHash: diffEvent.newHash,
                new: newRow,
            };
            return outputEvent;
        };

        for (const diffEvent of diffEvents) {
            // Handle deletes
            if (diffEvent.op === 'delete') {
                const outputEvent = {
                    op: diffEvent.op,
                    oldHash: diffEvent.oldHash,
                };
                this.#localDelete(diffEvent.oldHash);
                _diffEvents.set(diffEvent.oldHash, diffEvent);
                outputEvents.set(diffEvent.oldHash, outputEvent);
                continue;
            }

            // Handle updates & inserts
            if (diffEvent.op === 'update'
                && diffEvent.newHash !== diffEvent.oldHash) {
                idChanges.set(diffEvent.oldHash, diffEvent.newHash);
            } else if (diffEvent.op === 'insert'
                && effectiveLimit
                && !analysis.hasOrderByClause
                && this.#localRecords.size === effectiveLimit) {
                deferredInserts.add(diffEvent);
                continue;
            }

            // Add to logicalRecords
            const effectiveHash = diffEvent.oldHash || diffEvent.newHash;
            this.#localSet(effectiveHash, diffEvent.logicalRecord);

            // Enque events
            _diffEvents.set(effectiveHash, diffEvent);
            outputEvents.set(effectiveHash, await render(diffEvent));
        }

        if (idChanges.size) this.#localReindex(idChanges);

        let swaps = [];
        if (analysis.hasOrderByClause) {
            const [
                reorderedLocalRecords,
                sortedEntries,
                originalEntires
            ] = await this.#applySorting([...this.#localRecords], true);
            await this.#commitResult(commitMeta, reorderedLocalRecords);

            // Apply limit
            const splicedHashes = [];
            if (effectiveLimit && this.#localRecords.size >= effectiveLimit) {
                const excess = sortedEntries.slice(effectiveLimit);
                for (const [logicalHash, logicalRecord] of excess) {

                    // Remove from localRecords
                    this.#localRecords.delete(logicalHash);
                    splicedHashes.push(logicalHash);

                    // If part of events queue, remove
                    if (_diffEvents.get(logicalHash)?.op === 'insert') {
                        // Simply remove
                        _diffEvents.delete(logicalHash);
                        outputEvents.delete(logicalHash);
                    } else {
                        // Change updates to deletes
                        _diffEvents.set(logicalHash, {
                            op: 'delete',
                            oldHash: logicalHash,
                            old: logicalRecord,
                        });
                        outputEvents.set(logicalHash, {
                            op: 'delete',
                            oldHash: logicalHash,
                        });
                    }
                }
            }

            // Generate swaps
            const origianlLogicalKeys = originalEntires.map((e) => e[0]);
            const reorderedLocalKeys = sortedEntries.map((e) => e[0]);

            for (const [oldIdx, logicalHash] of origianlLogicalKeys.entries()) {

                // Handle spliced entry case (1)
                if (splicedHashes.includes(logicalHash)) continue;

                // Where does logicalHash now map to?
                let newIdx = reorderedLocalKeys.indexOf(logicalHash);
                let targetHash = origianlLogicalKeys[newIdx];

                // Handle spliced entry case (2)
                if (splicedHashes.includes(targetHash)) {
                    newIdx = reorderedLocalKeys.indexOf(targetHash);
                    targetHash = origianlLogicalKeys[newIdx];
                }
                if (newIdx !== oldIdx) {
                    swaps.push([logicalHash, targetHash]);
                }
            }
        } else {
            for (const diffEvent of deferredInserts) {
                // Limit reached?
                if (this.#localRecords.size === effectiveLimit) break;

                // Add to logicalRecords
                this.#localSet(diffEvent.newHash, diffEvent.logicalRecord);

                // Enque events
                _diffEvents.set(diffEvent.newHash, diffEvent);
                outputEvents.set(diffEvent.newHash, await render(diffEvent));
            }
        }

        // Emit events
        if (_diffEvents.size) {
            // System event
            const rawdiffEvent = { ...commitMeta, type: 'rawdiff', entries: [..._diffEvents.values()] };
            this.emit('rawdiff', rawdiffEvent);
        }

        if (outputEvents.size) {
            // WAL event
            const walEvent = { ...commitMeta, type: 'diff', entries: [...outputEvents.values()] };
            this.#wal.dispatch(walEvent);
        }

        if (swaps.length) {
            // WAL event
            const walEvent = { ...commitMeta, type: 'swap', entries: swaps };
            this.#wal.dispatch(walEvent);
        }

        return true;
    }

    async #commitResult(commitMeta, resultEntries, emit = false) {
        this.#localRecords.clear();
        for (const [logicalHash, logicalRecord] of resultEntries) {
            this.#localSet(logicalHash, logicalRecord);
        }
        if (emit) {
            // System event
            const rawresultEvent = { ...commitMeta, type: 'rawresult', entries: [...this.#localRecords] };
            this.emit('rawresult', rawresultEvent);

            // WAL event
            const walEvent = { ...commitMeta, type: 'result', ...await this.currentRendering() };
            this.#wal.dispatch(walEvent);
        }
    }

    async #applySorting(logicalRecords, withEntries = false) {
        const entries = logicalRecords;

        let decorated;
        if (this.#strategy.ssr) {
            decorated = entries.map((entry) => ({ entry, keys: entry[1].ord }));
        } else {
            decorated = await Promise.all(entries.map(async (entry) => {
                const keys = await Promise.all(this.#resolvedOrderElements.map(orderElement => {
                    return this.#exprEngine.evaluate(orderElement.expr(), entry[1], this.#queryCtx)
                }));
                return { entry, keys };
            }));
        }
        this.#exprEngine.applySorting(decorated, this.#resolvedOrderElements, this.#queryCtx);
        const _entries = decorated.map((e) => e.entry);

        const result = !Array.isArray(logicalRecords)
            ? new Map(_entries)
            : _entries;

        return withEntries
            ? [result, _entries, entries]
            : result;
    }
}
